import { useConditionFilters } from './useConditionFilters';

/**
 * Composable for condition editors that own the full add/remove/update cycle.
 * Wraps useConditionFilters and emits an updated condition on every change.
 *
 * @param TCondition - the full condition type (must have a `filters` field)
 * @param TValue     - union of all possible filter value types for this condition
 * @param TConfig    - filter config shape (must have `key: string` and `defaultValue: TValue`)
 */
export function useConditionEditor<
  TCondition extends { filters: object },
  TValue,
  TConfig extends { key: string; defaultValue: TValue },
>(
  getCondition: () => TCondition,
  emit: (event: 'update:condition', value: TCondition) => void,
  getFilterConfigs: () => TConfig[],
) {
  const { activeFilters, addOrRemove, update } = useConditionFilters<
    TConfig,
    TValue
  >(
    () => getCondition().filters as Partial<Record<string, TValue>>,
    getFilterConfigs,
  );

  const addFilter = (filterType: string) => {
    const condition = getCondition();
    const config = getFilterConfigs().find((c) => c.key === filterType);
    if (!config) return;

    emit('update:condition', {
      ...condition,
      filters: addOrRemove(
        condition.filters as Record<string, TValue | null>,
        filterType,
        config.defaultValue,
      ),
    } as TCondition);
  };

  const removeFilter = (filterType: string) => {
    const condition = getCondition();
    emit('update:condition', {
      ...condition,
      filters: addOrRemove(
        condition.filters as Record<string, TValue | null>,
        filterType,
        null,
      ),
    } as TCondition);
  };

  const updateFilter = (filterType: string, filter: TValue) => {
    const condition = getCondition();
    emit('update:condition', {
      ...condition,
      filters: update(
        condition.filters as Record<string, TValue>,
        filterType,
        filter,
      ),
    } as TCondition);
  };

  return { activeFilters, addFilter, removeFilter, updateFilter };
}
